import React from "react";
import { Container } from "react-bootstrap";
import styled, { keyframes } from "styled-components";
import { useTheme } from "components/shared/ThemeProvider";
import Seo from "components/shared/Seo";

// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

// Main container
const LearnMoreContainer = styled.div`
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
            : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #dee2e6 100%)"};
    min-height: 100vh;
    padding: 80px 0;
    position: relative;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${(props) =>
            props.$isDarkMode
                ? "radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)"
                : "radial-gradient(circle at 20% 80%, rgba(74, 144, 226, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.1) 0%, transparent 50%)"};
        pointer-events: none;
    }
`;

// Page header
const PageHeader = styled.div`
    text-align: center;
    margin-bottom: 80px;
    animation: ${fadeInUp} 0.8s ease-out;
`;

const PageTitle = styled.h1`
    font-size: 3.5rem;
    font-weight: 800;
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 20px;
    text-shadow: ${(props) =>
        props.$isDarkMode
            ? "0 4px 8px rgba(0, 0, 0, 0.3)"
            : "0 2px 4px rgba(0, 0, 0, 0.1)"};

    @media (max-width: 768px) {
        font-size: 2.5rem;
    }

    @media (max-width: 480px) {
        font-size: 2rem;
    }
`;

const PageSubtitle = styled.p`
    font-size: 1.3rem;
    color: ${(props) => (props.$isDarkMode ? "#a0a0a0" : "#6c757d")};
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        padding: 0 20px;
    }
`;

// Section styling
const Section = styled.section`
    margin-bottom: 100px;
    animation: ${fadeInUp} 0.8s ease-out;
    animation-delay: ${(props) => props.delay || "0s"};
    animation-fill-mode: both;

    @media (max-width: 768px) {
        margin-bottom: 60px;
    }
`;

const SectionTitle = styled.h2`
    font-size: 2.5rem;
    font-weight: 700;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
    margin-bottom: 30px;
    text-align: center;
    position: relative;

    &::after {
        content: "";
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;
        height: 4px;
        background: ${(props) =>
            props.$isDarkMode
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
        border-radius: 2px;
    }

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const SectionDescription = styled.p`
    font-size: 1.2rem;
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6c757d")};
    text-align: center;
    max-width: 800px;
    margin: 0 auto 50px;
    line-height: 1.7;

    @media (max-width: 768px) {
        font-size: 1.1rem;
        margin-bottom: 40px;
        padding: 0 20px;
    }
`;

// How to use section components
const TutorialGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 40px;
    margin-top: 50px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`;

const TutorialCard = styled.div`
    background: ${(props) =>
        props.$isDarkMode
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(255, 255, 255, 0.8)"};
    border-radius: 20px;
    padding: 30px;
    backdrop-filter: blur(10px);
    border: 1px solid
        ${(props) =>
            props.$isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)"};
    box-shadow: ${(props) =>
        props.$isDarkMode
            ? "0 20px 40px rgba(0, 0, 0, 0.3)"
            : "0 10px 30px rgba(0, 0, 0, 0.1)"};
    transition: all 0.3s ease;
    animation: ${scaleIn} 0.6s ease-out;
    animation-delay: ${(props) => props.delay || "0s"};
    animation-fill-mode: both;

    &:hover {
        transform: translateY(-10px);
        box-shadow: ${(props) =>
            props.$isDarkMode
                ? "0 30px 60px rgba(0, 0, 0, 0.4)"
                : "0 20px 40px rgba(0, 0, 0, 0.15)"};
    }
`;

const TutorialNumber = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.2rem;
    margin-bottom: 20px;
`;

const TutorialTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
    margin-bottom: 15px;
`;

const TutorialDescription = styled.p`
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6c757d")};
    line-height: 1.6;
    margin-bottom: 20px;
`;

const MediaPlaceholder = styled.div`
    width: 100%;
    height: 200px;
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)"
            : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)"};
    border: 2px dashed ${(props) => (props.$isDarkMode ? "#555" : "#ccc")};
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 15px;
    transition: all 0.3s ease;

    &:hover {
        border-color: ${(props) => (props.$isDarkMode ? "#777" : "#999")};
        background: ${(props) =>
            props.$isDarkMode
                ? "linear-gradient(135deg, #3d3d3d 0%, #4d4d4d 100%)"
                : "linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)"};
    }
`;

const PlaceholderIcon = styled.div`
    font-size: 2.5rem;
    color: ${(props) => (props.$isDarkMode ? "#777" : "#999")};
    margin-bottom: 10px;
`;

const PlaceholderText = styled.span`
    color: ${(props) => (props.$isDarkMode ? "#777" : "#999")};
    font-weight: 500;
    text-align: center;
`;

// Platform insights section components
const InsightsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 40px;
    margin-top: 50px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`;

const InsightCard = styled.div`
    background: ${(props) =>
        props.$isDarkMode
            ? "rgba(255, 255, 255, 0.05)"
            : "rgba(255, 255, 255, 0.8)"};
    border-radius: 20px;
    padding: 40px 30px;
    backdrop-filter: blur(10px);
    border: 1px solid
        ${(props) =>
            props.$isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)"};
    box-shadow: ${(props) =>
        props.$isDarkMode
            ? "0 20px 40px rgba(0, 0, 0, 0.3)"
            : "0 10px 30px rgba(0, 0, 0, 0.1)"};
    text-align: center;
    transition: all 0.3s ease;
    animation: ${scaleIn} 0.6s ease-out;
    animation-delay: ${(props) => props.delay || "0s"};
    animation-fill-mode: both;

    &:hover {
        transform: translateY(-10px);
        box-shadow: ${(props) =>
            props.$isDarkMode
                ? "0 30px 60px rgba(0, 0, 0, 0.4)"
                : "0 20px 40px rgba(0, 0, 0, 0.15)"};
    }
`;

const InsightIcon = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${(props) =>
        props.$isDarkMode
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "linear-gradient(135deg, #4a90e2 0%, #357abd 100%)"};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 25px;
`;

const InsightTitle = styled.h3`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${(props) => (props.$isDarkMode ? "#ffffff" : "#2c3e50")};
    margin-bottom: 15px;
`;

const InsightDescription = styled.p`
    color: ${(props) => (props.$isDarkMode ? "#b0b0b0" : "#6c757d")};
    line-height: 1.6;
`;

const LearnMore = () => {
    const { isDarkMode } = useTheme();

    return (
        <LearnMoreContainer $isDarkMode={isDarkMode}>
            <Seo title="Features" />
            <Container>
                {/* Page Header */}
                <PageHeader>
                    <PageTitle $isDarkMode={isDarkMode}>
                        Learn More About Wishliz
                    </PageTitle>
                    <PageSubtitle $isDarkMode={isDarkMode}>
                        Discover how our platform works and the principles that
                        make it powerful
                    </PageSubtitle>
                </PageHeader>

                {/* Section 1: How to Use Our Platform */}
                <Section delay="0.2s">
                    <SectionTitle $isDarkMode={isDarkMode}>
                        How to Use Our Platform
                    </SectionTitle>
                    <SectionDescription $isDarkMode={isDarkMode}>
                        Follow these simple steps to get the most out of
                        Wishliz. Watch our tutorials and see real examples of
                        how to maximize your experience.
                    </SectionDescription>

                    <TutorialGrid>
                        <TutorialCard delay="0.1s" $isDarkMode={isDarkMode}>
                            <TutorialNumber $isDarkMode={isDarkMode}>
                                1
                            </TutorialNumber>
                            <TutorialTitle $isDarkMode={isDarkMode}>
                                Getting Started
                            </TutorialTitle>
                            <TutorialDescription $isDarkMode={isDarkMode}>
                                Learn how to create your account and set up your
                                first wishlist. We'll walk you through the basic
                                features and interface.
                            </TutorialDescription>
                            <MediaPlaceholder $isDarkMode={isDarkMode}>
                                <PlaceholderIcon $isDarkMode={isDarkMode}>
                                    🎬
                                </PlaceholderIcon>
                                <PlaceholderText $isDarkMode={isDarkMode}>
                                    Tutorial Video Placeholder
                                    <br />
                                    (GIF or YouTube Video)
                                </PlaceholderText>
                            </MediaPlaceholder>
                        </TutorialCard>

                        <TutorialCard delay="0.2s" $isDarkMode={isDarkMode}>
                            <TutorialNumber $isDarkMode={isDarkMode}>
                                2
                            </TutorialNumber>
                            <TutorialTitle $isDarkMode={isDarkMode}>
                                Creating Wishlists
                            </TutorialTitle>
                            <TutorialDescription $isDarkMode={isDarkMode}>
                                Discover how to organize your wishes into
                                different categories and share them with friends
                                and family.
                            </TutorialDescription>
                            <MediaPlaceholder $isDarkMode={isDarkMode}>
                                <PlaceholderIcon $isDarkMode={isDarkMode}>
                                    📹
                                </PlaceholderIcon>
                                <PlaceholderText $isDarkMode={isDarkMode}>
                                    Demo Video Placeholder
                                    <br />
                                    (Interactive Tutorial)
                                </PlaceholderText>
                            </MediaPlaceholder>
                        </TutorialCard>

                        <TutorialCard delay="0.3s" $isDarkMode={isDarkMode}>
                            <TutorialNumber $isDarkMode={isDarkMode}>
                                3
                            </TutorialNumber>
                            <TutorialTitle $isDarkMode={isDarkMode}>
                                Advanced Features
                            </TutorialTitle>
                            <TutorialDescription $isDarkMode={isDarkMode}>
                                Explore powerful features like collaboration,
                                notifications, and advanced sharing options to
                                enhance your experience.
                            </TutorialDescription>
                            <MediaPlaceholder $isDarkMode={isDarkMode}>
                                <PlaceholderIcon $isDarkMode={isDarkMode}>
                                    🎥
                                </PlaceholderIcon>
                                <PlaceholderText $isDarkMode={isDarkMode}>
                                    Feature Showcase
                                    <br />
                                    (Step-by-step Guide)
                                </PlaceholderText>
                            </MediaPlaceholder>
                        </TutorialCard>

                        <TutorialCard delay="0.4s" $isDarkMode={isDarkMode}>
                            <TutorialNumber $isDarkMode={isDarkMode}>
                                4
                            </TutorialNumber>
                            <TutorialTitle $isDarkMode={isDarkMode}>
                                Tips & Best Practices
                            </TutorialTitle>
                            <TutorialDescription $isDarkMode={isDarkMode}>
                                Learn from experienced users about the best ways
                                to organize and maintain your wishlists for
                                maximum effectiveness.
                            </TutorialDescription>
                            <MediaPlaceholder $isDarkMode={isDarkMode}>
                                <PlaceholderIcon $isDarkMode={isDarkMode}>
                                    💡
                                </PlaceholderIcon>
                                <PlaceholderText $isDarkMode={isDarkMode}>
                                    Tips & Tricks Video
                                    <br />
                                    (Best Practices Guide)
                                </PlaceholderText>
                            </MediaPlaceholder>
                        </TutorialCard>
                    </TutorialGrid>
                </Section>

                {/* Section 2: Platform Insights */}
                <Section delay="0.4s">
                    <SectionTitle $isDarkMode={isDarkMode}>
                        Platform Insights
                    </SectionTitle>
                    <SectionDescription $isDarkMode={isDarkMode}>
                        Understanding the technology and principles behind
                        Wishliz will help you make the most of our platform
                        while being aware of its capabilities and limitations.
                    </SectionDescription>

                    <InsightsGrid>
                        <InsightCard delay="0.1s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                🏗️
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Underlying Architecture
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>
                                    Placeholder for technical details:
                                </strong>
                                <br />
                                Explain the core technology stack, database
                                design, and architectural decisions that power
                                Wishliz. Include information about scalability,
                                security, and performance optimizations.
                            </InsightDescription>
                        </InsightCard>

                        <InsightCard delay="0.2s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                🧠
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Core Principles
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>
                                    Placeholder for design philosophy:
                                </strong>
                                <br />
                                Detail the fundamental principles that guide our
                                platform design - user privacy, simplicity,
                                accessibility, and user-centric approach.
                                Explain how these principles influence features.
                            </InsightDescription>
                        </InsightCard>

                        <InsightCard delay="0.3s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                🔒
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Privacy & Security
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>
                                    Placeholder for security details:
                                </strong>
                                <br />
                                Outline our commitment to data protection,
                                encryption methods, privacy policies, and how
                                user data is handled and protected throughout
                                the platform.
                            </InsightDescription>
                        </InsightCard>

                        <InsightCard delay="0.4s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                ⚡
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Performance & Reliability
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>
                                    Placeholder for performance metrics:
                                </strong>
                                <br />
                                Discuss uptime guarantees, performance
                                benchmarks, load handling capabilities, and the
                                infrastructure that ensures reliable service
                                delivery.
                            </InsightDescription>
                        </InsightCard>

                        <InsightCard delay="0.5s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                ⚠️
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Current Limitations
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>Placeholder for limitations:</strong>
                                <br />
                                Be transparent about current platform
                                limitations, known issues, browser compatibility
                                requirements, and features that are in
                                development or planned.
                            </InsightDescription>
                        </InsightCard>

                        <InsightCard delay="0.6s" $isDarkMode={isDarkMode}>
                            <InsightIcon $isDarkMode={isDarkMode}>
                                🚀
                            </InsightIcon>
                            <InsightTitle $isDarkMode={isDarkMode}>
                                Future Roadmap
                            </InsightTitle>
                            <InsightDescription $isDarkMode={isDarkMode}>
                                <strong>Placeholder for roadmap:</strong>
                                <br />
                                Share our vision for the future, upcoming
                                features, planned improvements, and how user
                                feedback shapes our development priorities and
                                timeline.
                            </InsightDescription>
                        </InsightCard>
                    </InsightsGrid>
                </Section>
            </Container>
        </LearnMoreContainer>
    );
};

export default LearnMore;
